import {
  isCompanySignatureSignerForMission,
  type MissionLike,
} from '@/utils/companySignatureSigners'

/** Statuts terminaux : pas de cloche ni de compteur mission. */
const MISSION_NOTIFICATION_EXCLUDED_DISPLAY_STATUSES = new Set([
  'completed',
  'early-terminated',
  'terminated',
  'cancelled',
  'rejected',
  'deleted',
  'scheduled-early-termination',
])

export function isMissionNotificationEligible(displayStatus: string): boolean {
  if (!displayStatus) return true
  const normalized = displayStatus.toLowerCase().trim().replace(/_/g, '-')
  return !MISSION_NOTIFICATION_EXCLUDED_DISPLAY_STATUSES.has(normalized)
}

/**
 * Entreprise : inclure la mission dans le centre de notif / compteur pour le signataire
 * (contrat mission ou addendum / prolongation en attente) ou pour l'approbateur désigné.
 */
export function isMissionApproverForMission(
  mission: MissionLike | null | undefined,
  userId: string | null | undefined
): boolean {
  if (!mission || !userId) return false
  const approverId =
    mission.approver_id ??
    mission.approverId ??
    (typeof mission.approver === 'object' && mission.approver !== null
      ? (mission.approver as { id?: string }).id
      : null)
  return approverId != null && String(approverId) === String(userId)
}

export function missionNeedsCompanyNotificationAction(
  mission: MissionLike | null | undefined,
  displayStatus: string
): boolean {
  // La fiche mission rend le sidebar avant que le détail soit résolu (mission null) :
  // aucune action entreprise à signaler tant qu'on n'a pas la mission.
  if (!mission) return false

  if (!isMissionNotificationEligible(displayStatus)) return false

  if (displayStatus === 'pending-approval') return true

  if (displayStatus === 'pending-company-review') return true

  // C'est au freelance de signer / accepter / prolonger : pas d'action entreprise à notifier
  if (
    displayStatus === 'pending-freelancer-review' ||
    displayStatus === 'pending-freelancer-signature' ||
    displayStatus === 'pending-freelancer-extension' ||
    displayStatus === 'awaiting-freelancer-acceptance'
  ) {
    return false
  }

  const pendCoAdd = Number(
    mission.pending_addenda_count_for_company ?? mission.pendingAddendaCountForCompany ?? 0
  )
  const pendCoDoc = Number(
    mission.pending_documents_for_company_count ?? mission.pendingDocumentsForCompanyCount ?? 0
  )

  if (pendCoAdd === 0 && pendCoDoc === 0) return false

  return true
}

/**
 * Côté entreprise : l'utilisateur doit voir la cloche (liste, fiche) ou recevoir l'entrée
 * dans le centre de notifications (approbateur désigné ou signataire entreprise).
 */
export function isMissionCompanyNotificationRecipient(
  mission: MissionLike | null | undefined,
  userId: string | null | undefined,
  displayStatus: string
): boolean {
  if (!mission || !userId || !missionNeedsCompanyNotificationAction(mission, displayStatus)) {
    return false
  }
  if (displayStatus === 'pending-approval') {
    return isMissionApproverForMission(mission, userId)
  }
  return isCompanySignatureSignerForMission(mission, userId)
}
